import React from 'react';
import styled from 'styled-components';

import { FEATURES } from 'appConstants';
import { usePerx3Redesign } from 'hooks';
import useFeatureFlags from 'hooks/useFeatureFlags';

import IntelligenceScaleContext, { IIntelligenceScale } from './IntelligenceScaleContext';
import IntelligenceThemeScope from './IntelligenceThemeScope';
import { tokens } from './tokens';

export interface IIntelligenceContentScopeProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * PLA-8815: the dark region is the content area of the Intelligence route only. This wrapper
 * renders inside the route element, i.e. below `<Layout.Content>`, so its custom properties and
 * surface rules cannot reach the sider or the outer layout. Matching the mockup's full-bleed dark
 * top would need a class on the shared app layout, which is deliberately out of scope.
 */
const ContentSurfaceStyled = styled.div<{ $ownFontFamily: boolean }>`
  background-color: ${tokens.color['bg-app']};
  color: ${tokens.color['text-primary']};
  /* Roboto is applied here and not on the token carrier, so it stays a rule of the Intelligence
     content area only: the rest of the dashboard declares no family and keeps antd's stack. Under
     perx_3_redesign the suite drops the override and inherits the app's own face instead. */
  ${({ $ownFontFamily }) => ($ownFontFamily ? `font-family: ${tokens.font['font-family']};` : '')}
  /* NOTE: no font-size here. This wrapper contains the whole route, so a 12.5px body token would
     become the inherited size for every antd descendant, which sizes its controls against the
     14px LESS base. The body role belongs on the Intelligence text primitives instead. */
  /* Keeps the dark surface flush when a route renders less than a screenful. Bare 100vh
     over-reports behind mobile Safari's toolbars (conventions/components/cross-browser.md § 7),
     so the pre-dvh fallback is the parent's height, never a viewport unit. */
  min-height: 100%;
  min-height: 100dvh;

  /* antd's reset paints headings and links for the light theme, leaving them unreadable here. */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${tokens.color['text-primary']};
  }

  a {
    color: ${tokens.color['accent-blue']};
  }
`;

const IntelligenceContentScope: React.FC<IIntelligenceContentScopeProps> = ({
  children,
  className
}) => {
  const isPerx3 = usePerx3Redesign();
  const features = useFeatureFlags();
  const responsive = Boolean(features[FEATURES.INTELLIGENCE_RESPONSIVE_LAYOUT.key]);
  const scale = React.useMemo<IIntelligenceScale>(() => ({ responsive }), [responsive]);

  return (
    <IntelligenceThemeScope responsiveScale={responsive}>
      {/* The caller's class lands on the suite's surface, not on the token carrier: spacing or
          positioning applied outside it would render on the app background. */}
      <IntelligenceScaleContext.Provider value={scale}>
        <ContentSurfaceStyled
          $ownFontFamily={!isPerx3}
          className={className}
          data-testid="intelligence-content-scope"
        >
          {children}
        </ContentSurfaceStyled>
      </IntelligenceScaleContext.Provider>
    </IntelligenceThemeScope>
  );
};

export default IntelligenceContentScope;
